import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { HeadContentEditModalProps } from './types';
import { TABS } from './types';
import { useHeadContentForm } from './useHeadContentForm';
import { BasicSEOTab } from './BasicSEOTab';
import { SocialMediaTab } from './SocialMediaTab';
import { AdvancedTab } from './AdvancedTab';

export const HeadContentEditModal: React.FC<HeadContentEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  isLoading = false,
  siteId = '',
  siteOgImage,
}) => {
  console.log('🔍 HeadContentEditModal: Received initialData:', {
    hasInitialData: !!initialData,
    dataKeys: Object.keys(initialData),
    hasTitle: !!initialData.title,
    hasDescription: !!initialData.description,
    fullData: initialData,
  });

  const [activeTab, setActiveTab] = useState('basic');
  const {
    formData,
    handleInputChange,
    handleAddCustomMeta,
    handleCustomMetaChange,
    handleRemoveCustomMeta,
    handleAddStructuredData,
    handleStructuredDataChange,
    handleRemoveStructuredData,
    getCleanedData,
  } = useHeadContentForm(isOpen, initialData);

  const handleSubmit = () => {
    const cleanedData = getCleanedData();
    console.log('📤 Sending SEO data to backend:', {
      cleanedData,
      hasTitle: !!cleanedData.title,
      hasDescription: !!cleanedData.description,
      hasKeywords: !!cleanedData.keywords,
      hasCanonicalUrl: !!cleanedData.canonicalUrl,
      hasFavicon: !!cleanedData.favicon,
      allKeys: Object.keys(cleanedData),
    });
    onSave(cleanedData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Configure SEO & Meta Tags</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mt-4">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeTab === 'basic' && (
            <BasicSEOTab formData={formData} onInputChange={handleInputChange} />
          )}
          {activeTab === 'social' && (
            <SocialMediaTab
              formData={formData}
              onInputChange={handleInputChange}
              siteId={siteId}
              siteOgImage={siteOgImage}
            />
          )}
          {activeTab === 'advanced' && (
            <AdvancedTab
              formData={formData}
              onAddCustomMeta={handleAddCustomMeta}
              onCustomMetaChange={handleCustomMetaChange}
              onRemoveCustomMeta={handleRemoveCustomMeta}
              onAddStructuredData={handleAddStructuredData}
              onStructuredDataChange={handleStructuredDataChange}
              onRemoveStructuredData={handleRemoveStructuredData}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeadContentEditModal;
